import type {
  FilterField,
  FilterOption,
  SelectFilterField,
} from '@/components/common/AdvancedFilter';
import { LOADING_PLACEHOLDER } from '@/constants/app.constants';
import type { PromoItem } from '@/features/promotion/types/promotion.types';
import { getDateFromToFilterFields } from '@/utils/filterFields';
import { BOOLEAN_FILTER_VALUE, toFilterOptions } from '@/utils/filters';
import {
  CALL_RESULT_OPTIONS,
  DECISION_OPTIONS,
  TEACHER_ANSWER_OPTIONS,
} from '../constants/marketPromotion.constants';
import type { MisCaller } from '../types/marketPromotion.types';
import type { DonationProject } from '../types/marketPromotionP2.types';

const DONATION_NAME_LABEL = 'Donation Name';
const ALL_DONATIONS_PLACEHOLDER = 'All Donations';

interface DonationProjectFieldOptions {
  isRequired: boolean;
}

const DONATION_PROJECT_LABEL = 'Donation Project';

function toDonationProjectOptions(projects: DonationProject[] | undefined): FilterOption[] {
  return (
    projects?.map((project) => ({
      label: project.itemName,
      value: String(project.promoItemId),
      isActive: project.isActive,
    })) ?? []
  );
}

export function getDonationProjectFilterField(
  projects: DonationProject[] | undefined,
  isLoading: boolean,
  { isRequired }: DonationProjectFieldOptions,
): SelectFilterField {
  return {
    key: 'promoItemId',
    label: DONATION_PROJECT_LABEL,
    type: 'select',
    placeholder: isLoading ? LOADING_PLACEHOLDER : 'Pick a project',
    options: toDonationProjectOptions(projects),
    searchable: true,
    isRequired,
  };
}

export function getDonationProjectsFilterField(
  projects: DonationProject[] | undefined,
  isLoading: boolean,
): FilterField {
  return {
    key: 'promoItemIds',
    label: DONATION_PROJECT_LABEL,
    type: 'multiSelect',
    placeholder: isLoading ? LOADING_PLACEHOLDER : 'All Projects',
    options: toDonationProjectOptions(projects),
  };
}

export function getDonationItemsFilterField(items: PromoItem[] | undefined): FilterField {
  return {
    key: 'promoItemIds',
    label: DONATION_NAME_LABEL,
    type: 'multiSelect',
    placeholder: ALL_DONATIONS_PLACEHOLDER,
    options: toFilterOptions(items),
  };
}

export function getDecisionStatusFilterField(label = 'Status'): FilterField {
  return {
    key: 'approveTypeNos',
    label,
    type: 'multiSelect',
    placeholder: 'All',
    options: toFilterOptions(DECISION_OPTIONS),
  };
}

const MIS_VERIFY_FILTER_OPTIONS: FilterOption[] = [
  { value: BOOLEAN_FILTER_VALUE.yes, label: 'Verified' },
  { value: BOOLEAN_FILTER_VALUE.no, label: 'Not Verified' },
];

const CALL_DATE_HINT = 'Pick Verified in Verify Status first';

export function getMisCallFilterFields(
  callers: MisCaller[] | undefined,
  isVerifiedPicked: boolean,
): FilterField[] {
  const callDateFields = getDateFromToFilterFields({
    fromKey: 'callDateFrom',
    toKey: 'callDateTo',
    labelPrefix: 'Call Date',
  }).map((field) => ({
    ...field,
    disabled: !isVerifiedPicked,
    disabledHint: CALL_DATE_HINT,
  }));

  return [
    {
      key: 'callStatus',
      label: 'Call Result',
      type: 'select',
      placeholder: 'All',
      options: toFilterOptions(CALL_RESULT_OPTIONS),
    },
    {
      key: 'teacherFeedback',
      label: 'Teacher Answer',
      type: 'select',
      placeholder: 'All',
      options: toFilterOptions(TEACHER_ANSWER_OPTIONS),
    },
    {
      key: 'isMisVerified',
      label: 'Verify Status',
      type: 'select',
      placeholder: 'All',
      options: MIS_VERIFY_FILTER_OPTIONS,
    },
    {
      key: 'calledBy',
      label: 'Called By',
      type: 'select',
      placeholder: 'All',
      options: toFilterOptions(callers),
      searchable: true,
    },
    ...callDateFields,
  ];
}
